'use client'

import { useEffect, useMemo, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '@/components/ui/ui-dialog'
import { Card } from '@/components/ui/card'
import {
  FiArrowLeft,
  FiArrowRight,
  FiCheck,
  FiCreditCard,
  FiShield,
  FiTrendingUp,
  FiZap
} from 'react-icons/fi'
import { FaWhatsapp } from 'react-icons/fa'
import { cn } from '@/lib/utils'
import { toast } from 'react-hot-toast'

interface PlanApi {
  id: number
  nombrePlan: string
  precio: number
  consultasMensuales: number
  descripcion?: string
  estado?: number
}

interface PlanSelectionModalProps {
  isOpen: boolean
  onClose: () => void
  currentPlanId: number
  userEmail: string
}

function formatConsultas(consultasMensuales: number) {
  if (consultasMensuales === -1) return 'Consultas ilimitadas'
  return `${consultasMensuales.toLocaleString('es-PE')} consultas`
}

function getPlanTone(nombrePlan: string) {
  const plan = nombrePlan.toLowerCase()
  if (plan.includes('gratuito')) {
    return {
      glow: 'from-slate-500/10 via-slate-400/5 to-transparent',
      badge: 'bg-slate-100 text-slate-700',
      price: 'text-slate-900',
      button: 'bg-slate-900 hover:bg-slate-800 text-white',
      ring: 'hover:border-slate-300'
    }
  }
  if (plan.includes('básico') || plan.includes('basico')) {
    return {
      glow: 'from-blue-500/15 via-cyan-400/5 to-transparent',
      badge: 'bg-blue-50 text-blue-700',
      price: 'text-blue-700',
      button: 'bg-blue-600 hover:bg-blue-700 text-white',
      ring: 'hover:border-blue-300'
    }
  }
  if (plan.includes('pro')) {
    return {
      glow: 'from-violet-500/15 via-fuchsia-400/5 to-transparent',
      badge: 'bg-violet-50 text-violet-700',
      price: 'text-violet-700',
      button: 'bg-violet-600 hover:bg-violet-700 text-white',
      ring: 'hover:border-violet-300'
    }
  }
  return {
    glow: 'from-emerald-500/15 via-teal-400/5 to-transparent',
    badge: 'bg-emerald-50 text-emerald-700',
    price: 'text-emerald-700',
    button: 'bg-emerald-600 hover:bg-emerald-700 text-white',
    ring: 'hover:border-emerald-300'
  }
}

export default function PlanSelectionModal({ isOpen, onClose, currentPlanId, userEmail }: PlanSelectionModalProps) {
  const [planes, setPlanes] = useState<PlanApi[]>([])
  const [selectedPlan, setSelectedPlan] = useState<PlanApi | null>(null)
  const [showPaymentOptions, setShowPaymentOptions] = useState(false)
  const [paymentMethod, setPaymentMethod] = useState<'yape' | 'mercadopago' | 'bcp' | null>(null)
  const [loading, setLoading] = useState(false)
  const [loadingPlanes, setLoadingPlanes] = useState(false)

  useEffect(() => {
    if (!isOpen) return

    const fetchPlanes = async () => {
      setLoadingPlanes(true)
      try {
        const res = await fetch('/api/planes')
        const data = await res.json()
        if (data.success) {
          const activos = Array.isArray(data.data)
            ? data.data
                .filter((plan: PlanApi) => plan.estado === 1 || typeof plan.estado === 'undefined')
                .sort((a: PlanApi, b: PlanApi) => a.precio - b.precio)
            : []
          setPlanes(activos)
        } else {
          toast.error(data.error || 'No se pudieron cargar los planes')
        }
      } catch {
        toast.error('Error al cargar planes')
      } finally {
        setLoadingPlanes(false)
      }
    }

    fetchPlanes()
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) {
      setSelectedPlan(null)
      setShowPaymentOptions(false)
      setPaymentMethod(null)
    }
  }, [isOpen])

  const currentPlan = useMemo(
    () => planes.find((plan) => plan.id === currentPlanId) ?? null,
    [planes, currentPlanId]
  )

  const handleSelectPlan = (plan: PlanApi) => {
    const canUpgrade = currentPlan ? Number(plan.precio) > Number(currentPlan.precio) : plan.id > currentPlanId
    if (!canUpgrade) return
    setSelectedPlan(plan)
    setShowPaymentOptions(true)
  }

  const handleBack = () => {
    if (paymentMethod) {
      setPaymentMethod(null)
    } else {
      setShowPaymentOptions(false)
      setSelectedPlan(null)
    }
  }

  const processPayment = async (method: 'yape' | 'mercadopago' | 'bcp') => {
    if (!selectedPlan) return
    setPaymentMethod(method)

    if (method === 'mercadopago') {
      setLoading(true)
      try {
        const res = await fetch('/api/user/plan/upgrade', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ planId: selectedPlan.id })
        })
        const data = await res.json()
        if (data.init_point) {
          window.location.href = data.init_point
        } else {
          toast.error(data.error || 'Error al generar link de pago')
        }
      } catch {
        toast.error('Error de conexión')
      } finally {
        setLoading(false)
      }
    }
  }

  const handleWhatsApp = (type: 'yape' | 'mercadopago' | 'bcp') => {
    if (!selectedPlan || type === 'mercadopago') return
    const mensaje = `Hola, envío el comprobante de mi ${selectedPlan.nombrePlan}, S/ ${selectedPlan.precio} mensuales. Mi correo es ${userEmail}`
    const telefono = '51917575562'
    window.open(`https://wa.me/${telefono}?text=${encodeURIComponent(mensaje)}`, '_blank')
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl p-0 overflow-hidden border border-slate-200/80 shadow-[0_32px_80px_rgba(15,23,42,0.22)] rounded-[28px] bg-white">
        <DialogHeader className="px-7 py-6 border-b border-slate-100 bg-white">
          <DialogTitle className="text-[30px] leading-none font-bold text-slate-900">
            {showPaymentOptions ? (selectedPlan ? `Activar ${selectedPlan.nombrePlan}` : 'Selecciona un plan') : 'Mejora tu Plan'}
          </DialogTitle>
          <DialogDescription className="pt-2 text-[15px] text-slate-500">
            {showPaymentOptions ? 'Elige el método de pago que mejor te acomode.' : 'Solo puedes subir a un plan superior según tu suscripción actual.'}
          </DialogDescription>
        </DialogHeader>

        <div className="bg-[linear-gradient(180deg,#f8fafc_0%,#f8fafc_42%,#ffffff_100%)]">
          <div className="px-6 py-6 max-h-[72vh] overflow-y-auto">
            {!showPaymentOptions ? (
              loadingPlanes ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                  {Array.from({ length: 4 }).map((_, index) => (
                    <div key={index} className="h-72 rounded-[24px] bg-slate-100 animate-pulse" />
                  ))}
                </div>
              ) : (
                <div className="space-y-5">
                  {currentPlan && (
                    <div className="flex items-center justify-between rounded-[22px] border border-slate-200 bg-white px-5 py-4">
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-slate-400">Plan actual</p>
                        <p className="mt-1 text-lg font-semibold text-slate-900">{currentPlan.nombrePlan}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-slate-400">Consultas</p>
                        <p className="mt-1 text-base font-semibold text-slate-900">{formatConsultas(currentPlan.consultasMensuales)}</p>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                    {planes.map((plan) => {
                      const isCurrent = plan.id === currentPlanId
                      const canUpgrade = currentPlan ? Number(plan.precio) > Number(currentPlan.precio) : plan.id > currentPlanId
                      const isBlocked = !isCurrent && !canUpgrade
                      const tone = getPlanTone(plan.nombrePlan)
                      const itemDescripcion = plan.descripcion?.trim() || 'Acceso a todas las APIs y soporte.'

                      return (
                        <Card
                          key={plan.id}
                          className={cn(
                            'relative overflow-hidden rounded-[24px] border bg-white p-5 transition-all duration-300',
                            isCurrent
                              ? 'border-emerald-300 shadow-[0_20px_50px_rgba(16,185,129,0.16)]'
                              : isBlocked
                                ? 'border-slate-200 opacity-70'
                                : cn('border-slate-200 shadow-sm hover:-translate-y-1 hover:shadow-[0_18px_45px_rgba(37,99,235,0.14)]', tone.ring)
                          )}
                        >
                          <div className={cn('pointer-events-none absolute inset-0 bg-gradient-to-br', tone.glow)} />

                          <div className="relative">
                            <div className="mb-4 flex items-start justify-between gap-3">
                              <div>
                                <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-slate-400">Suscripción</p>
                                <h3 className="mt-2 text-[26px] leading-none font-bold text-slate-900 uppercase tracking-tight">
                                  {plan.nombrePlan}
                                </h3>
                              </div>
                              {isCurrent ? (
                                <span className="shrink-0 rounded-full bg-emerald-600 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.16em] text-white shadow-sm ring-1 ring-emerald-700/10">
                                  Plan actual
                                </span>
                              ) : isBlocked ? (
                                <span className="rounded-full bg-slate-100 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">
                                  No disponible
                                </span>
                              ) : (
                                <span className={cn('rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.22em]', tone.badge)}>
                                  Upgrade
                                </span>
                              )}
                            </div>

                            <div className="mb-4">
                              <div className="flex items-end gap-1">
                                <span className={cn('text-[42px] leading-none font-black', tone.price)}>
                                  S/ {Number(plan.precio).toFixed(2)}
                                </span>
                                <span className="pb-1 text-sm font-medium text-slate-400">/ mes</span>
                              </div>
                            </div>

                            <div className={cn('mb-4 inline-flex items-center gap-2 rounded-2xl px-3 py-2 text-sm font-semibold', tone.badge)}>
                              <FiZap className="h-4 w-4" />
                              {formatConsultas(plan.consultasMensuales)}
                            </div>

                            <div className="space-y-3 text-[14px] text-slate-600">
                              <div className="flex items-start gap-2">
                                <FiCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-500" />
                                <span className="leading-6">{itemDescripcion}</span>
                              </div>
                              <div className="flex items-start gap-2">
                                <FiShield className="mt-0.5 h-4 w-4 flex-shrink-0 text-slate-500" />
                                <span>Soporte prioritario y continuidad del servicio.</span>
                              </div>
                            </div>

                            <button
                              disabled={!canUpgrade}
                              onClick={() => handleSelectPlan(plan)}
                              className={cn(
                                'mt-6 flex h-11 w-full items-center justify-center gap-2 rounded-2xl text-sm font-bold transition-all',
                                canUpgrade
                                  ? tone.button
                                  : isCurrent
                                    ? 'bg-emerald-100 text-emerald-700 cursor-default'
                                    : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                              )}
                            >
                              {isCurrent ? 'Tu plan actual' : canUpgrade ? 'Subir a este plan' : 'No puedes bajar'}
                              {canUpgrade && <FiTrendingUp className="h-4 w-4" />}
                            </button>
                          </div>
                        </Card>
                      )
                    })}
                  </div>
                </div>
              )
            ) : (
              <div className="mx-auto max-w-4xl space-y-5">
                {!paymentMethod ? (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <button
                      onClick={() => processPayment('mercadopago')}
                      disabled={loading}
                      className="group rounded-[24px] border border-blue-200 bg-white p-5 text-left shadow-sm transition-all hover:-translate-y-1 hover:shadow-[0_20px_45px_rgba(37,99,235,0.15)]"
                    >
                      <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-blue-600 transition-transform group-hover:scale-105">
                        <FiCreditCard className="h-7 w-7" />
                      </div>
                      <h4 className="text-xl font-bold text-slate-900">Mercado Pago</h4>
                      <p className="mt-2 text-sm leading-6 text-slate-500">Pago automático e inmediato con redirección segura.</p>
                      <div className="mt-5 flex items-center gap-2 text-sm font-semibold text-blue-600">
                        Continuar
                        <FiArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                      </div>
                    </button>

                    <button
                      onClick={() => processPayment('yape')}
                      className="group rounded-[24px] border border-violet-200 bg-white p-5 text-left shadow-sm transition-all hover:-translate-y-1 hover:shadow-[0_20px_45px_rgba(139,92,246,0.16)]"
                    >
                      <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-violet-50 text-violet-600 transition-transform group-hover:scale-105">
                        <FiZap className="h-7 w-7" />
                      </div>
                      <h4 className="text-xl font-bold text-slate-900">Yape o Plin</h4>
                      <p className="mt-2 text-sm leading-6 text-slate-500">Escanea el QR y envía el comprobante desde WhatsApp.</p>
                      <div className="mt-5 flex items-center gap-2 text-sm font-semibold text-violet-600">
                        Ver instrucciones
                        <FiArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                      </div>
                    </button>

                    <button
                      onClick={() => processPayment('bcp')}
                      className="group rounded-[24px] border border-amber-200 bg-white p-5 text-left shadow-sm transition-all hover:-translate-y-1 hover:shadow-[0_20px_45px_rgba(245,158,11,0.16)]"
                    >
                      <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-50 text-amber-600 transition-transform group-hover:scale-105">
                        <FiShield className="h-7 w-7" />
                      </div>
                      <h4 className="text-xl font-bold text-slate-900">Transferencia BCP</h4>
                      <p className="mt-2 text-sm leading-6 text-slate-500">Realiza la transferencia y comparte la constancia.</p>
                      <div className="mt-5 flex items-center gap-2 text-sm font-semibold text-amber-600">
                        Ver datos de pago
                        <FiArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                      </div>
                    </button>
                  </div>
                ) : (
                  <div className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_24px_60px_rgba(15,23,42,0.12)]">
                    <div className="grid grid-cols-1 md:grid-cols-[220px,1fr] gap-6 items-center">
                      <div className="rounded-[26px] bg-slate-50 p-5">
                        <div
                          className={cn(
                            'flex aspect-square items-center justify-center rounded-[22px] text-4xl font-black text-white shadow-inner',
                            paymentMethod === 'yape' ? 'bg-gradient-to-br from-violet-500 to-fuchsia-600' : 'bg-gradient-to-br from-amber-500 to-orange-600'
                          )}
                        >
                          {paymentMethod === 'yape' ? 'QR' : 'BCP'}
                        </div>
                        <p className={cn(
                          'mt-4 text-center text-sm font-bold',
                          paymentMethod === 'yape' ? 'text-violet-600' : 'text-amber-600'
                        )}>
                          {paymentMethod === 'yape' ? '917575562' : '19110337582000'}
                        </p>
                      </div>

                      <div className="space-y-4">
                        <div className="inline-flex rounded-full bg-blue-50 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.24em] text-blue-700">
                          Pago manual
                        </div>
                        <div>
                          <h4 className="text-[28px] leading-none font-bold text-slate-900">Activación de Suscripción</h4>
                          <p className="mt-3 max-w-xl text-[15px] leading-7 text-slate-600">
                            Para activar tu <strong>{selectedPlan?.nombrePlan}</strong> por <strong>S/ {Number(selectedPlan?.precio || 0).toFixed(2)}</strong> mensuales, completa el pago y envía el comprobante para validación rápida.
                          </p>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                            <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">Plan</p>
                            <p className="mt-1 text-sm font-semibold text-slate-900">{selectedPlan?.nombrePlan}</p>
                          </div>
                          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                            <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">Monto</p>
                            <p className="mt-1 text-sm font-semibold text-slate-900">S/ {Number(selectedPlan?.precio || 0).toFixed(2)} / mes</p>
                          </div>
                        </div>

                        <button
                          onClick={() => paymentMethod && handleWhatsApp(paymentMethod)}
                          className="flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-emerald-500 text-sm font-bold text-white shadow-[0_18px_40px_rgba(16,185,129,0.22)] transition-all hover:bg-emerald-600"
                        >
                          <FaWhatsapp className="h-5 w-5" />
                          Enviar comprobante
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <button
                  onClick={handleBack}
                  className="mx-auto flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
                >
                  <FiArrowLeft className="h-4 w-4" />
                  {paymentMethod ? 'Volver a métodos' : 'Volver a planes'}
                </button>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
